<template>
  <div class="searchresult-area">
    <div class="result-controller flex items-center justify-between">
      <div class="all-check-wrapper flex items-center">
        <q-checkbox
          v-if="props.searchResult.attoIdList.length !== 0"
          v-model="checked"
          color="primary"
          @update:model-value="
            (value: any) => (value ? (checkValues = props.searchResult.attoIdList) : (checkValues = []))
          "
        />
        <p>
          <span>{{ props.searchResult.attoIdList.length }}</span
          >件
          <span
            v-if="props.searchResult.searchMaxCount > Number($t('validation.search_max_count'))"
            style="font-weight: normal"
            >/ {{ props.searchResult.searchMaxCount }}件</span
          >
        </p>
      </div>
      <div class="q-gutter-sm">
        <q-btn
          v-if="props.searchResult.attoIdList.length !== 0"
          icon="mdi-download"
          class="export-btn"
          color="primary"
          outline
          no-caps
          @click="displayOutExcelDialog"
          >Excel出力</q-btn
        >
        <q-btn
          v-if="props.searchResult.attoIdList.length !== 0"
          icon="mdi-download"
          color="primary"
          outline
          no-caps
          class="export-btn"
          @click="displayOutSdfDialog"
          >SDFile出力</q-btn
        >
      </div>
    </div>
    <!-- 検索結果 -->
    <q-list>
      <q-item v-for="(item, i) in props.searchResult.searchResultInfo" :key="i" class="mb-1">
        <q-item-section class="flex justify-between">
          <div class="left-area">
            <div class="result-title">【検索条件】</div>
            <div class="flex items-center query-data">
              <!-- 構造検索の場合 -->
              <div
                v-if="item.searchResultConditionsDto.symbolic_query_type === 'STRUCTURE'"
                class="img-container emphable-img"
              >
                <AtomImage :exatoms="item.searchResultConditionsDto.symbolic_query" />
              </div>

              <!-- キー検索の場合 -->
              <div v-else class="query-container">
                <p>{{ item.searchResultConditionsDto.symbolic_query }}</p>
              </div>
            </div>
          </div>
          <div class="right-area flex">
            <div
              v-if="item.searchResultConditionsDto.querysearchResultCount === 0"
              class="result-wrapper search-result-none linkable"
            >
              <div>検索条件に該当する商品はありませんでした。</div>
              <div>
                検索結果にご不明な点がございましたら、
                <router-link :to="{ name: 'inquiry' }" class="text-link">お問い合わせ</router-link
                >ください。
              </div>
            </div>
            <div
              v-else-if="
                item.searchResultConditionsDto.querysearchResultCount !== 0 &&
                item.searchResultDetailList === null
              "
              class="result-wrapper search-result-none"
            >
              <p>検索結果が他の条件で上限数を超えたため、検索しませんでした。</p>
              <p>当条件のみで検索を実施してください。</p>
            </div>
            <div
              v-else-if="
                item.searchResultConditionsDto.querysearchResultCount === null &&
                item.searchResultDetailList.length === 0
              "
              class="result-wrapper search-result-none linkable"
            >
              <div>検索条件に該当する商品はありませんでした。</div>
              <div>
                検索結果にご不明な点がございましたら、
                <router-link :to="{ name: 'inquiry' }" class="text-link">お問い合わせ</router-link
                >ください。
              </div>
            </div>
            <div
              v-for="(itemDetail, itd) in item.searchResultDetailList"
              :key="itd"
              class="result-wrapper"
            >
              <ChemCard
                :check-values="checkValues"
                :result="itemDetail"
                @change-check="changeCheckBox"
                @on-detail="onDetail"
              />
            </div>
          </div>
        </q-item-section>
      </q-item>
    </q-list>
    <!-- Excel出力確認ダイアログ -->
    <AlertDialog
      :display="isOutExcelDialog"
      :level="dialogLevel"
      :confirmation="isConfFlg"
      :title="dialogTitle"
      :text="dialogText"
      @complete="isOutExcelDialog = false"
      @confirm="downloadExcel()"
      @close="isOutExcelDialog = false"
    />
    <!-- SDF出力確認ダイアログ -->
    <AlertDialog
      :display="isOutSdfDialog"
      :level="dialogLevel"
      :confirmation="isConfFlg"
      :title="dialogTitle"
      :text="dialogText"
      @complete="isOutSdfDialog = false"
      @confirm="downloadSdf()"
      @close="isOutSdfDialog = false"
    />
    <DetailResultDialog
      v-if="detailResultDialogFlg"
      :result-atto-id-list="props.searchResult.attoIdList"
      :select-atto-id="selectAttoId"
      :detail-result-flg="detailResultDialogFlg"
      :reagent-flg="props.reagentFlg"
      :categories="props.categories"
      :suppliers="props.suppliers"
      @close-detail-dialog="detailResultDialogFlg = false"
      @detail-result-dialog-flg="detailResultDialogFlg = $event"
    />
    <!-- ダイアログ -->
    <AlertDialog
      :display="dialogDispFlg"
      level="error"
      confirmation
      :title="dialogTitle"
      :text="dialogText"
      @complete="dialogDispFlg = false"
    />
  </div>
</template>
<script setup lang="ts">
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { Loading } from 'quasar';
import saveAs from 'file-saver';
import ChemCard from '../card/ChemCard.vue';
import AtomImage from '../images/AtomImage.vue';
import AlertDialog from '../dialogs/AlertDialog.vue';
import http from '../../common/axios-common';
import ResultStatusCode from '../../common/code/ResultStatusCode';
import DetailResultDialog from '../dialogs/DetailResultDialog.vue';
import base64toBlob from '../../common/utils/fileUtils';
import HttpResponseStatusCode from '../../common/code/HttpResponseStatusCode';

// 検索結果
interface Result {
  atto_id: string;
  atto_supplier_count: number;
  order_count: number;
  condition_id: number;
  result_id: number;
  result_number: number;
  structure: string;
}

// 検索条件
interface Conditions {
  condition_id: number;
  condition_number: number;
  symbolic_query: string;
  symbolic_query_type: string;
  querysearchResultCount: number;
}

// 検索結果情報
interface SearchResultInfo {
  searchResultConditionsDto: Conditions;
  searchResultDetailList: Array<Result>;
}

// 検索Response
interface SearchResponse {
  attoIdList: Array<string>;
  searchResultInfo: Array<SearchResultInfo>;
  searchMaxCount: number;
}

// サプライヤInterface
interface Supplier {
  label: string;
  value: string;
}

interface Props {
  // 検索結果
  searchResult: SearchResponse;
  // 試薬フラグ
  reagentFlg: boolean;
  // カテゴリー
  categories: number[];
  // サプライヤ
  suppliers: Supplier[];
}
const props = defineProps<Props>();

const router = useRouter();

// ダイアログ表示
const dialogDispFlg = ref<boolean>(false);

// ダイアログテキスト
const dialogText = ref<Array<string>>(['']);

// ダイアログタイトル
const dialogTitle = ref<string>('');

// Dialog表示レベル
const dialogLevel = ref('');

// 確認Dialogフラグ
const isConfFlg = ref(false);

// Sdf出力確認Dialog表示フラグ
const isOutSdfDialog = ref(false);

// Excel出力確認Dialog表示フラグ
const isOutExcelDialog = ref(false);

// チェックボックス選択値
const checkValues = ref<Array<string>>([]);

// チェックボックス全選択フラグ
const checked = ref<boolean>(false);

// 詳細結果の表示フラグ
const detailResultDialogFlg = ref(false);

// 詳細結果に表示するATTOID
const selectAttoId = ref<string>('');

// チェックボックス変更処理
const changeCheckBox = (attoIdList: Array<string>) => {
  if (JSON.stringify(props.searchResult.attoIdList) === JSON.stringify(attoIdList.sort())) {
    checked.value = true;
  } else {
    checked.value = false;
  }
  checkValues.value = attoIdList;
};

// Excel出力確認ダイアログ表示
const displayOutExcelDialog = () => {
  // チェックボックスが1つもチェックされていない場合
  if (checkValues.value.length === 0) {
    dialogText.value = ['出力対象を選択してください。'];
    dialogTitle.value = 'Excel出力エラー';
    dialogDispFlg.value = true;
    isOutExcelDialog.value = false;
  } else {
    dialogTitle.value = 'Excel出力確認';
    dialogLevel.value = 'information';
    dialogText.value = ['検索結果のExcelファイルを出力します。', 'よろしいですか？'];
    isConfFlg.value = false;
    isOutExcelDialog.value = true;
  }
};

// SDF出力確認ダイアログ表示
const displayOutSdfDialog = () => {
  // チェックボックスが1つもチェックされていない場合
  if (checkValues.value.length === 0) {
    dialogText.value = ['出力対象を選択してください。'];
    dialogTitle.value = 'SDFile出力エラー';
    dialogDispFlg.value = true;
    isOutSdfDialog.value = false;
  } else {
    dialogTitle.value = 'SDFile出力確認';
    dialogLevel.value = 'information';
    dialogText.value = ['検索結果のSDFileを出力します。', 'よろしいですか？'];
    isConfFlg.value = false;
    isOutSdfDialog.value = true;
  }
};

// Excelダウンロード
const downloadExcel = () => {
  // チェックボックスが1つもチェックされていない場合
  if (checkValues.value.length === 0) {
    dialogText.value = ['出力対象を選択してください。'];
    dialogTitle.value = 'Excel出力エラー';
    dialogDispFlg.value = true;
    isOutExcelDialog.value = false;
  } else {
    // Loading Start
    Loading.show();

    const formData = new FormData();
    checkValues.value.forEach((attoId: string) => formData.append('atto_ids', attoId));
    formData.append('filetype', 'XLSX');

    // ダウンロード
    http
      .post('/search-result/download', formData)
      .then((response) => {
        if (response.data.common.status === ResultStatusCode.ERROR) {
          router.push({ name: 'error' });
        } else {
          const blob = new Blob(
            [
              base64toBlob(
                response.data.body,
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
              ),
            ],
            {}
          );
          saveAs(blob, response.data.file_name);
          // ダイアログ表示
          dialogTitle.value = 'Excel出力完了';
          dialogLevel.value = 'information';
          dialogText.value = ['検索結果のExcelファイルを出力しました。'];
          isConfFlg.value = true;
          isOutExcelDialog.value = true;
        }
      })
      .catch((err) => {
        if (err.response.status === HttpResponseStatusCode.SESSIONTIMEOUT) {
          // セッションタイムアウトの場合
          router.push({ name: 'login', query: { timeout: 'timeout' } });
        } else {
          // その他のエラーの場合
          //  エラー画面に遷移
          router.push({ name: 'error' });
        }
      })
      .finally(() => {
        // Loading End
        Loading.hide();
      });
  }
};

// SDFダウンロード
const downloadSdf = () => {
  // チェックボックスが1つもチェックされていない場合
  if (checkValues.value.length === 0) {
    dialogText.value = ['出力対象を選択してください。'];
    dialogTitle.value = 'SDFile出力エラー';
    dialogDispFlg.value = true;
    isOutSdfDialog.value = false;
  } else {
    // Loading Start
    Loading.show();

    const formData = new FormData();
    checkValues.value.forEach((attoId: string) => formData.append('atto_ids', attoId));
    formData.append('filetype', 'SDF');

    // ダウンロード
    http
      .post('/search-result/download', formData)
      .then((response) => {
        if (response.data.common.status === ResultStatusCode.ERROR) {
          router.push({ name: 'error' });
        } else {
          const blob = new Blob([base64toBlob(response.data.body, 'text/plain')], {});
          saveAs(blob, response.data.file_name);
          // ダイアログ表示
          dialogTitle.value = 'SDFile出力完了';
          dialogLevel.value = 'information';
          dialogText.value = ['検索結果のSDFileを出力しました。'];
          isConfFlg.value = true;
          isOutSdfDialog.value = true;
        }
      })
      .catch((err) => {
        if (err.response.status === HttpResponseStatusCode.SESSIONTIMEOUT) {
          // セッションタイムアウトの場合
          router.push({ name: 'login', query: { timeout: 'timeout' } });
        } else {
          // その他のエラーの場合
          //  エラー画面に遷移
          router.push({ name: 'error' });
        }
      })
      .finally(() => {
        // Loading End
        Loading.hide();
      });
  }
};

// 詳細ボタン押下
const onDetail = (attoId: string) => {
  selectAttoId.value = attoId;
  detailResultDialogFlg.value = true;
};
</script>
<style scoped>
p,
label,
span {
  margin: 0;
  padding: 0;
}

.searchresult-area {
  width: 94%;
  margin: 2% auto 0;
  padding-bottom: 0;
}

.searchresult-area .all-check-wrapper {
  margin-left: -10px;
  margin-bottom: -14px;
}

.searchresult-area .all-check-wrapper p {
  margin-top: 4px;
}

.searchresult-area .all-check-wrapper span {
  margin-right: 6px;
  font-size: 1.2rem;
  line-height: 1.2rem;
  font-weight: bold;
  margin-left: 5px;
  letter-spacing: 4px;
}

.searchresult-area .q-list {
  margin-top: 15px;
}

.searchresult-area .q-item {
  border: 2px solid var(--q-lightColorBorder);
  margin-bottom: 10px;
  min-width: 1265px;
}

.searchresult-area .q-item:last-child {
  margin-bottom: 5%;
}

.searchresult-area .q-item__section {
  flex-direction: row;
}

.searchresult-area .result-title {
  font-weight: bold;
  font-size: 1rem;
  line-height: 1rem;
  letter-spacing: 3px;
  color: var(--q-plainBlack);
}

.searchresult-area .right-area .result-wrapper.search-result-none {
  border: none;
  width: 100%;
  max-width: unset;
  padding-bottom: 25px;
  padding: 25px 40px;
}

.searchresult-area .search-result-none.linkable .text-link {
  cursor: pointer;
  color: var(--q-pitchBlack);
  text-decoration: none;
  border-bottom: 1px solid var(--q-pitchBlack);
}

.searchresult-area .right-area .result-wrapper.search-result-none.linkable .text-link:hover {
  color: var(--q-primary);
  border-color: var(--q-primary);
}

.searchresult-area .search-result-none .q-item__section {
  flex-direction: column;
}

.searchresult-area .search-result-none.linkable * {
  font-size: 1.1rem;
  font-weight: bold;
  margin: 0;
  line-height: 1.8rem;
  letter-spacing: 2px;
}

.searchresult-area .left-area {
  width: 190px;
  padding-top: 6px;
}

.searchresult-area .left-area .img-container {
  width: 90%;
  position: relative;
}

.searchresult-area .left-area .query-container {
  font-size: 1rem;
  margin: 10px 0 0 1rem;
  letter-spacing: 2px;
}

.searchresult-area .left-area .query-container p {
  word-break: break-all;
}

.searchresult-area .right-area {
  width: calc(100% - 200px);
  margin-left: 10px;
  padding: 0;
  border-left: 2px solid var(--q-lightColorBorder);
  flex-wrap: wrap;
}

.searchresult-area .right-area .result-wrapper {
  width: 24%;
  margin-left: 1%;
  border: 2px solid var(--q-lightColorBorder);
  margin-top: 10px;
  max-width: 270px;
  margin-bottom: 10px;
  padding-bottom: 12px;
  border-radius: 3px;
}
</style>
<style>
.bg-black.shadow-4 {
  font-size: 14px !important;
}

.searchresult-area .result-controller .export-btn .q-icon.mdi-download {
  margin-right: 4px;
}
</style>
